import React, {ReactElement, useCallback, useRef} from "react";
import {Chart, ChartArea, ChartData, ChartOptions, LegendElement, Plugin, Scale} from "chart.js";
import {Line} from "react-chartjs-2";
import classnames from "classnames";

import {ChartComponent} from ".";
import {nFormatter} from "../../format";
import {Button} from "../input";
import {AnyColor} from "../../types";

import styles from "./chart.module.scss";

const getGradient = (
	context: CanvasRenderingContext2D,
	chartArea: ChartArea,
	color: AnyColor
): CanvasGradient => {
	const gradient = context.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
	if (styles[color]) gradient.addColorStop(1, styles[`transparent-${color}`]);

	return gradient;
};

const Options = (showLegend?: boolean, subtitle?: string): ChartOptions<"line"> => ({
	responsive: true,
	plugins: {
		subtitle: subtitle
			? {
					display: true,
					text: subtitle,
					align: "center",
					position: "left",
					padding: 10,
			  }
			: undefined,
		legend: {
			display: showLegend,
			align: "start",
			labels: {
				boxWidth: 18,
				boxHeight: 18,
				padding: 10,
				usePointStyle: true,
				pointStyle: "rect",
			},
		},
		title: {
			display: false,
			padding: {
				bottom: 10,
			},
		},
		tooltip: {
			displayColors: false,
			callbacks: {
				label: () => "",
				title: context => `${context[0].parsed.y}` ?? context[0].label,
			},
		},
	},
	scales: {
		y: {
			grid: {
				color: "#E5E5EF",
			},
			border: {
				dash: [2, 4],
			},
			ticks: {
				precision: 0,
				callback: value => nFormatter(value, 2),
			},
		},
		x: {
			ticks: {
				maxTicksLimit: 11,
			},
		},
	},
});

const legendPlugin: Plugin = {
	beforeInit(chart: Chart & {legend: LegendElement<"line"> & Scale}) {
		const originalFit = chart.legend.fit;
		chart.legend.fit = function fit() {
			originalFit.bind(chart.legend)();
			this.height += 10;
		};
	},
	id: "legendPlugin",
};

export const GradientChart = ({
	data,
	labels,
	showLegend,
	subtitle,
	className,
	chartName,
}: ChartComponent & {chartName: string}): ReactElement => {
	const formatData = (): ChartData<"line"> => ({
		labels,
		datasets: data.map(({data, label, color}) => ({
			fill: true,
			label,
			data: data as number[],
			pointStyle: "circle",
			pointRadius: 6,
			borderColor: styles[color],
			backgroundColor: ({chart: {ctx, chartArea}}) => chartArea && getGradient(ctx, chartArea, color),
		})),
	});

	const chartRef = useRef<Chart<"line", unknown> | null>(null);
	const clickHandler = useCallback(() => {
		const link = document.createElement("a");
		link.download = `${chartName}.jpeg`;
		link.href = chartRef.current?.toBase64Image("image/jpeg", 1) ?? "";
		link.click();
	}, [chartRef, chartName]);

	return (
		<div className={styles.container}>
			<Line
				ref={chartRef}
				plugins={[legendPlugin]}
				className={classnames(className)}
				options={Options(showLegend, subtitle)}
				data={formatData()}
			/>

			<Button className={styles.downloadButton} icon="download" onClick={clickHandler} />
		</div>
	);
};
